import React from 'react';
import _ from 'lodash';
import {Field, Form, FormValidation, resetSubmitting, getFormData, untouch} from '@nexios/frontend-forms';
import parse from 'html-react-parser';
import replaceNewLinesForHtml from '../../utils/replaceNewLines';
import parseText from '../../utils/parseText';
import {Helmet} from 'react-helmet';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';

import {cleanseAddress, closeDialog, renderDialog, savePetition} from '../../reducer';

// Utils
import parseAuthorizeLabel from '../../utils/parsePrivacyStatement';

// Hooks
import useScrollToTop from '../../hooks/useScrollToTop';

// Components
import Box from '../../components/Box';
import BottomImage from '../../components/BottomImage';
import Button from '../../components/Button';
import ButtonContainer from '../../components/ButtonContainer';
import Checkbox from '../../components/Checkbox';
import DateInput from '../../components/DateInput';
import Dropdown from '../../components/Dropdown';
import ErrorMessage from '../../components/ErrorMessage';
import PaymentFailedMessage from '../../components/PaymentFailedMessage';
import FieldWrapper from '../../components/FieldWrapper';
import Input from '../../components/Input';
import InputAmount from '../../components/InputAmount';
import Label from '../../components/Label';
import SubLabel from '../../components/SubLabel';
import LayoutContainer from '../../components/LayoutContainer';
import Logo from '../../components/Logo';
import MainContainer from '../../components/MainContainer';
import ModalDialog from '../../components/ModalDialog';
import PaymentMethodButtonContainer from '../../components/PaymentMethodButtonContainer';
import ProgressBar from '../../components/ProgressBar';
import RadioButtonContainer from '../../components/RadioButtonContainer';
import SinglePeriod from '../../components/SingePeriod';
import Teaser from '../../components/Teaser';
import Telemarketing from '../../components/Telemarketing';
import NewsletterOptIn from '../../components/NewsletterOptIn';
import TextArea from '../../components/TextArea';
import Title from '../../components/Title';
import Validation from '../../components/Validation';
import {AddressRow, StyledAddressLabel, HouseNumberRow, FoundAddress, FoundAddressTitle, NoFoundAddress} from '../../components/Address';

// Local imports
import DirectDebitText from './DirectDebitText';
import NameRow from './NameRow';
import DonationConfirmationAmountText from './DonationConfirmationAmountText';
import SelectedAmountText from '../../components/SelectedAmountText';
import SelectedFrequencyContent from '../../components/SelectedFrequencyContent';
import createFormConfig from './createFormConfig';
import RadioButton from '../../components/RadioButton';
import FormLayout from '../../components/FormLayout';
import TextToHtml from '../../components/TextToHtml';
import SmsOptOut from '../../components/SmsOptOut';
import PrivacyLabel from '../../components/PrivacyLabel';
import styled, {withTheme} from 'styled-components';
import {wrap as wrapTitle} from '../../components/TitleWrapper';
import DJSBSideBar from '../../components/DJSBSideBar';
import orderContactBlocks from '../../utils/orderContactBlocks';

const Petition1 = ({formContext, save, history, cleanse, resetSubmittingForm, progressBar, renderDialog, closeDialog, isDialogVisible, untouchAmountFields, theme}) => {
  useScrollToTop();
  let cleansing = false;

  const formConfig = createFormConfig({formContext, cleansing, cleanse, resetSubmittingForm, save, history, untouchAmountFields, theme});

  const genderBlock = (
    <Field
      noMargin
      label='Geslacht'
      name='gender'
      hideLabel={formContext.hideGenderFieldLabel}
      list='genders'
      component={FieldWrapper}
      inputComponent={(props) => {
        return formContext.genderDisplay === 'dropdown'
          ? <Dropdown fieldName='gender' valueName='gender' {...props} placeholder='Kiezen...' />
          : <RadioButtonContainer fieldName='gender' valueName='gender' list={props.list} {...props} />;
      }}
    />
  );

  const nameBlock = (
    <div id='name-container'>
      {!formContext.hideNameFieldLabel && <Label label='Naam' />}
      <div className='marginBottomTen'>
        <Field
          name='firstName'
          component={FieldWrapper}
          inputComponent={Input}
          placeholder={'Voornaam'}
          hideValidation
          hideLabel={formContext.hideNameFieldLabel}
          autoComplete='given-name'
        />
      </div>
      {!formContext.hideNameFieldLabel && <NameRow>
        <Field
          name='initials'
          component={() => <SubLabel label='Voorletters' isOptional={!formContext.isInitialsRequired} />}
          hideValidation
        />
        <Field
          name='infix'
          component={() => <SubLabel label='Tussenvoegsel' isOptional />}
          hideValidation
        />
        <Field
          name='lastName'
          component={() => <SubLabel label='Achternaam' />}
          hideValidation
        />
      </NameRow>}
      <NameRow>
        <Field
          name='initials'
          component={FieldWrapper}
          inputComponent={Input}
          placeholder={'Voorletters'}
          hideValidation
          hideLabel={formContext.hideNameFieldLabel}
          inline
          autoComplete='00db6d76-1330-4b71-89aa-72fe7b9f238a'
        />
        <Field
          name='infix'
          component={FieldWrapper}
          inputComponent={Input}
          placeholder={'Tussenvoegsel'}
          hideValidation
          hideLabel={formContext.hideNameFieldLabel}
          autoComplete='90ab3315-3929-4643-b78d-9de3e727eadf'
        />
        <Field
          name='lastName'
          component={FieldWrapper}
          inputComponent={Input}
          placeholder={'Achternaam'}
          hideValidation
          hideLabel={formContext.hideNameFieldLabel}
          autoComplete='family-name'
        />
      </NameRow>

      <Field
        name='firstName'
        noMargin
        component={Validation} />
      <Field
        name='initials'
        noMargin
        component={Validation} />
      <Field
        name='infix'
        noMargin
        component={Validation} />
      <Field
        name='lastName'
        noMargin
        component={Validation} />
    </div>
  );

  const variableCheckboxBlock = (
    <Field
      noMargin
      name='variableCheckboxAnswer'
      label={formContext.variableCheckboxLabel || '-'}
      component={FieldWrapper}
      inputComponent={Checkbox}
      hideLabel
    />
  );

  const addressBlock = formContext.person.isAddressVisible && (
    <div id='address-container'>
      {!formContext.hideAddressFieldLabel && <StyledAddressLabel>
        <Label label='Adres' isOptional={!formContext.person.isAddressRequired} />
      </StyledAddressLabel>}
      <AddressRow>
        <div>
          {!formContext.hideAddressFieldLabel && <Field
            name='postalCode'
            component={() => <SubLabel label='Postcode' />}
            hideValidation
          />}
          <Field
            name='postalCode'
            component={FieldWrapper}
            inputComponent={Input}
            placeholder={'Postcode'}
            inline
            hideValidation
            hideLabel={formContext.hideAddressFieldLabel}
            autoComplete='postal-code'
          />
        </div>
        <div>
          {!formContext.hideAddressFieldLabel && <HouseNumberRow>
            <Field
              name='houseNumber_i'
              component={() => <SubLabel label='Huisnummer' />}
              hideValidation
            />
            <Field
              name='houseNumberAddition'
              component={() => <SubLabel label='Toevoeging' isOptional />}
              hideValidation
            />
          </HouseNumberRow>}
          <HouseNumberRow>
            <Field
              name='houseNumber_i'
              component={FieldWrapper}
              inputComponent={Input}
              placeholder={'Huisnummer'}
              inline
              hideValidation
              hideLabel={formContext.hideAddressFieldLabel}
              inputType='tel'
              autoComplete='05d2ae54-5b99-4418-9d38-fde0ab6d553f'
            />
            <Field
              name='houseNumberAddition'
              component={FieldWrapper}
              inputComponent={Input}
              placeholder={'Toevoeging'}
              hideValidation
              hideLabel={formContext.hideAddressFieldLabel}
              autoComplete='f1a99033-6af3-4aa2-94db-1de7ff1af61d'
            />
          </HouseNumberRow>
        </div>
      </AddressRow>
      <Field
        name='postalCode'
        noMargin
        component={Validation} />
      <Field
        name='houseNumber_i'
        noMargin
        component={Validation} />
      <Field
        name='houseNumberAddition'
        noMargin
        component={Validation} />
      <FoundAddress>
        <FoundAddressTitle>Adres:</FoundAddressTitle>
        <Field name='noAddress' component={() => <NoFoundAddress>Wordt automatisch aangevuld</NoFoundAddress>} />
        <Field
          component={({value}) => <div className='addressBox'>{value}&nbsp;</div>}
          name='street' />
        <Field
          component={({value}) => <div className='addressBox'>{value}</div>}
          name='city' />
      </FoundAddress>
    </div>
  );

  const phoneBlock = (
    <Field
      label='Telefoonnummer'
      name='telephone'
      component={props => <FieldWrapper {...props} noMargin>
        {(formContext.telemarketingOptIn === 'text' || formContext.telemarketingOptIn === 'checkbox' || formContext.telemarketingOptIn === 'dropdown') && <Telemarketing
          noMargin
          telemarketingOptIn={formContext.telemarketingOptIn}
          telemarketingInfoText={formContext.telemarketingInfoText}
          telemarketingCheckBoxText={formContext.telemarketingCheckBoxText}
        />}
      </FieldWrapper>}
      inputComponent={Input}
      placeholder='Telefoonnummer'
      inputType='tel'
      autoComplete='tel'
      hideLabel={formContext.hidePhoneFieldLabel}
    />
  );

  const dateOfBirthBlock = (
    <Field
      noMargin
      label='Geboortedatum'
      name='dateOfBirth'
      component={FieldWrapper}
      inputComponent={DateInput}
      autoComplete='5d65176f-1708-4b09-93c4-9b9e4f97e4dc'
      hideLabel={formContext.hideDateOfBirthFieldLabel}
    />
  );

  const emailBlock = (
    <Field
      noMargin
      label='E-mailadres'
      name='email'
      inputComponent={Input}
      component={props => <FieldWrapper {...props}>
        <NewsletterOptIn label={formContext.newsletterText} noMargin />
      </FieldWrapper>}
      placeholder='E-mailadres'
      inputType='email'
      autoComplete='email'
      hideLabel={formContext.hideEmailFieldLabel}
    />
  );

  let content = (
    <>
      <Box position={formContext.textPosition}
        hasTeaser={!_.isNil(formContext.pageTeaserHeader) || !_.isNil(formContext.pageTeaserBodyText) || !_.isEmpty(formContext.pageTeaserCarouselItems)}>
        <Title title={formContext.step1Header}><ProgressBar progress={progressBar} /></Title>
        <Form
          autoComplete='off'
          formInstanceKey='donate'
          formContext={formContext}
          formConfig={formConfig}>
          <PaymentFailedMessage formContext={formContext} />
          {!theme?.error?.showBelowBodyText && <FormValidation
            component={ErrorMessage}
            errorMessageTemplate={formContext.errorMessageTemplate}
            formContext={formContext}
            errorMessage='Je hebt nog niet alle velden ingevuld.' />}

          <div className='widgetContent'>
            <FormLayout>
              {!_.isNil(formContext.step1BodyText) && <div>
                {theme?.title?.showInBody && <TextToHtml text={formContext.step1Header} wrap={wrapTitle} />}
                <TextToHtml text={formContext.step1BodyText} />
              </div>}
              {theme?.error?.showBelowBodyText && <FormValidation
                component={ErrorMessage}
                errorMessageTemplate={formContext.errorMessageTemplate}
                formContext={formContext}
                errorMessage='Je hebt nog niet alle velden ingevuld.' />}
              <Field
                label={formContext.variableFieldLabel}
                name='variableFieldAnswer'
                inputComponent={TextArea}
                component={FieldWrapper}
                placeholder={formContext.variableFieldPlaceholder}
              />
              {orderContactBlocks(formContext.person.fieldOrder, {
                gender: genderBlock,
                name: nameBlock,
                variableCheckbox: variableCheckboxBlock,
                address: addressBlock,
                phone: phoneBlock,
                dateOfBirth: dateOfBirthBlock,
                email: emailBlock
              }).map(({key, node}) => <React.Fragment key={key}>{node}</React.Fragment>)}

              <Field
                name='showDonationFields'
                component={FieldWrapper}
                inputComponent={(props) => {
                  return <>
                    {!_.isNil(formContext.stepUpTextHtml_txt) && <div><TextToHtml text={formContext.stepUpTextHtml_txt} /></div>}
                    <div style={{display: 'flex', flexDirection: 'column', gap: '5px'}}>
                      <RadioButton
                        name={props.name}
                        key={`${props.name}-yes`}
                        hasErrors={props.hasErrors}
                        isChecked={props.value === true}
                        withBorder
                        label={formContext.showDonationFieldsLabelYes || 'Ja, ik draag met mijn donatie bij aan een oplossing'}
                        validate={props.validate}
                        disabled={props.disabled}
                        setValue={() => props.setValue(true)} />
                      <RadioButton
                        name={props.name}
                        key={`${props.name}-no`}
                        hasErrors={props.hasErrors}
                        isChecked={props.value === false}
                        withBorder
                        label={formContext.showDonationFieldsLabelNo || 'Nee, ik wil niet bijdragen'}
                        validate={props.validate}
                        disabled={props.disabled}
                        setValue={() => props.setValue(false)} />
                    </div>
                  </>;
                }}
              />

              <Field
                list='frequencies'
                name='frequency'
                component={(props) => {
                  const list = _.get(props, ['list']);
                  // do not render options if there is only 1 frequency
                  if (_.size(list) < 2 && formContext.frequencyDisplay === 'dropdown') {
                    return null;
                  }
                  const enrichedFrequencies = list.map(item => ({...item, ribbon: _.find(formContext.frequencies, f => f.value === item.value)?.ribbon || ''}));
                  return <>
                    {formContext.frequencyDisplay === 'dropdown' && <Dropdown {...props} />}
                    {formContext.frequencyDisplay === 'radiobutton' && <RadioButtonContainer {...props} />}
                    {formContext.frequencyDisplay === 'button' && <ButtonContainer fieldName='frequency' list={enrichedFrequencies} />}
                  </>;
                }}
              />

              <Field
                name='frequency'
                component={() => <SelectedFrequencyContent formContext={formContext} />}
              />

              <Field
                list='amounts'
                name='amount_m'
                component={(props) => {
                  const list = props.list.map(a => {
                    a.ribbon = a.value.ribbon;
                    a.icon = a.value.icon;
                    a.fieldName = a.value.fieldName;
                    a.value = a.value.value;
                    return a;
                  });

                  return <ButtonContainer
                    fieldName='amount_m'
                    valueName='amount_m'
                    list={list} />;
                }}
              />

              <Field
                name='setCustomAmountVisible'
                component={({updateField}) => <div className='centered'>
                  <button type='button' className='link' onClick={() => {
                    updateField('isCustomAmountVisible', true);
                    updateField('amount_m', null);
                  }}>
                    Of kies een ander bedrag
                  </button>
                </div>
                }
              />

              <Field
                name='amount_m'
                component={() => <SelectedAmountText formContext={formContext} />}
              />

              <Field
                label={formContext.customAmountDisplay !== 'inline' ? 'Vul het bedrag in dat je wilt doneren' : null}
                name='customAmount_m'
                placeholder={formContext.customAmountDisplay === 'inline' ? 'Of vul hier een ander bedrag in' : '0,00'}
                component={FieldWrapper}
                inputComponent={InputAmount}
              />

              <Field
                name='setCustomAmountHidden'
                component={({updateField}) => <div className='centered'>
                  <button type='button' className='link' onClick={() => {
                    updateField('isCustomAmountVisible', false);
                    updateField('customAmount_m', null);
                  }}>Of kies bedrag
                  </button>
                </div>}
              />

              <Field
                list='periods'
                component={(props) => {
                  const list = _.get(props, ['list']);
                  // do not render options if there is only 1 frequency
                  if (_.size(list) === 0) {
                    return null;
                  }
                  if (_.size(list) === 1) {
                    return <SinglePeriod label={list[0].label} />;
                  }
                  return <>
                    {formContext.periodDisplay === 'dropdown' && <Dropdown {...props} placeholder='Selecteer een periode...' />}
                    {formContext.periodDisplay === 'radiobutton' && <RadioButtonContainer {...props} />}
                    {formContext.periodDisplay === 'button' && <ButtonContainer fieldName='period' list={list} />}
                  </>;
                }}
                name='period'
              />

              {formContext.showDonationConfirmationAmountText && <DonationConfirmationAmountText />}

              <Field
                name='paymentMethod'
                component={(props) => {
                  return <PaymentMethodButtonContainer
                    fieldName='paymentMethod'
                    valueName='paymentMethod'
                    list={props.list} />;
                }}
                list='paymentMethods'
              />
              <Field
                list='idealBanks'
                name='bank'
                component={FieldWrapper}
                placeholder={'Kies je bank'}
                inputComponent={Dropdown}
              />
              <Field
                list='creditCardBanks'
                name='creditCardBank'
                component={FieldWrapper}
                placeholder={'Kies je creditcard'}
                inputComponent={Dropdown}
              />
              <Field
                label='Rekeningnummer'
                name='iban'
                component={FieldWrapper}
                inputComponent={Input}
                placeholder='NL11ABNA012345678'
                forceUppercase />
              <Field
                name='mandateText'
                component={MandateTextConnected} />

              {!theme.hideDirectDebitTextOnPetitionPage && <DirectDebitText formContext={formContext} />}

              {!theme.hidePrivacyLabelOnPetitionPage && formContext.authorizeAsCheckbox && <Field
                noMargin
                hideLabel
                largeValidationGap
                name='authorize'
                component={FieldWrapper}
                inputComponent={Checkbox}
                label={<Field
                  name='authorizeLabel'
                  component={({value}) =>
                    <PrivacyLabel page='petition' text={parseAuthorizeLabel(value, renderDialog)} />} />} />}

              {!theme.hidePrivacyLabelOnPetitionPage && !formContext.authorizeAsCheckbox && <Field
                name='authorizeLabel'
                component={({value}) =>
                  <PrivacyLabel page='petition' text={parseAuthorizeLabel(value, renderDialog)} />} />}

              <div className={'donationReason'} aria-hidden='true'>
                <Field
                  name='donationReason'
                  component={FieldWrapper}
                  inputComponent={Input}
                  label={'Reden donatie'}
                  placeholder={'Reden donatie'}
                  hideValidation
                  tabIndex={'-1'}
                  autoComplete={'e0e3896c-f25b-422d-8da1-bdc2466652d8'}
                />
              </div>

              <FormValidation
                type='submit'
                label={formContext.petitionCallToActionLabel || 'Ondertekenen'}
                component={Button}
              />
              {!theme.hidePrivacyLabelOnPetitionPage && formContext.privacyConfirmationTextPetition &&
                <PrivacyLabel page='petition' text={parseAuthorizeLabel(formContext.privacyConfirmationTextPetition, renderDialog)} />}
              {formContext.telemarketingOptIn === 'button' && <Telemarketing
                noMargin
                telemarketingOptIn={formContext.telemarketingOptIn}
                telemarketingInfoText={formContext.telemarketingInfoText}
                telemarketingCheckBoxText={formContext.telemarketingCheckBoxText}
              />}
              {formContext.entryCallToAction &&
                <div className='centered'>
                  {formContext.entryCallToActionUrl ? <a className='link' href={formContext.entryCallToActionUrl} rel='noopener noreferrer'>{formContext.entryCallToAction}</a> : formContext.entryCallToAction}
                </div>}
            </FormLayout>
            <BottomImage url={formContext.bottomImageUrl} altText={formContext.bottomImageAltText} />
          </div>
        </Form>
      </Box>
      <SmsOptOut text={formContext.smsOptOutText} processedText={formContext.smsOptOutProcessedText} />
    </>
  );

  if (isDialogVisible) {
    content = <ModalDialog close={() => {
      closeDialog();
    }}>
      <div dangerouslySetInnerHTML={{__html: formContext.privacyStatement}} />
    </ModalDialog>;
  }

  return <>
    <Helmet>
      <title>{formContext.petitionBrowserTitle}</title>
    </Helmet>
    <MainContainer
      backgroundImageUrl={formContext.backgroundImageUrl}
      backgroundImageTabletUrl={formContext.backgroundImageTabletUrl}
      backgroundImageMobileUrl={formContext.backgroundImageMobileUrl}
    />
    <LayoutContainer
      position={formContext.textPosition}
      header={<div>
        <div style={{float: 'left', display: 'inline-block'}}>
          <Logo url={formContext.websiteUrl} />
        </div>
      </div>}
      content={content}
      sidebar={<>
        {theme?.djsb?.showDJSB && <DJSBSideBar formContext={formContext} />}
        <Teaser header={formContext.pageTeaserHeader} body={formContext.pageTeaserBodyText} formContext={formContext} />
      </>
      }
      faq={formContext.frequentlyAskedQuestions}
      footer={formContext.footer}
      formContext={formContext}
    />
  </>;
};

const mapStateToProps = (state) => {
  return {
    formContext: state.reducer.formContext,
    isDialogVisible: state.reducer.isDialogVisible
  };
};

const mapDispatchToProps = (dispatch) => ({
  save: (history, submittedFormData) => {
    dispatch(savePetition(history, submittedFormData));
  },
  cleanse: (data, callback) => {
    dispatch(cleanseAddress(data, callback));
  },
  resetSubmittingForm: () => dispatch(resetSubmitting('donate')),
  renderDialog: (e) => {
    e.stopPropagation();
    e.preventDefault();
    dispatch(renderDialog());
  },
  closeDialog: () => {
    dispatch(closeDialog());
  },
  untouchAmountFields: () => {
    dispatch(untouch({instanceKey: 'donate', field: 'customAmount_m'}));
    dispatch(untouch({instanceKey: 'donate', field: 'amount_m'}));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTheme(Petition1)));

const MandateTextStyled = styled.div`
  font-size: ${props => props.theme?.membership?.mandateText?.fontSize};
  color: ${props => props.theme?.membership?.mandateText?.color};
  font-style: ${props => props.theme?.membership?.mandateText?.fontStyle};
`;
const MandateText = ({formContext, formData}) => <MandateTextStyled>
  {parse(replaceNewLinesForHtml(parseText({...formContext, ...formData}, formContext.petitionMandateText)))}
</MandateTextStyled>;

const mapStateToProps2 = (state) => {
  return {
    formContext: state.reducer.formContext,
    formData: getFormData(state, 'donate')
  };
};
const MandateTextConnected = connect(mapStateToProps2)(MandateText);
